import { _ as _arrayWithHoles, a as _unsupportedIterableToArray, b as _nonIterableRest } from './objects-1c42d56c.esm.js';
export { e as entries, f as fromEntries, i as includes, m as mapValues, o as omit, p as pick } from './objects-1c42d56c.esm.js';

/**
 * Assertion for ensuring exhaustive handling (i.e: `switch` statements that handle every possible type)
 *
 * @example
 * ```ts
 * export const handleType = (type: 'foo' | 'bar' | 'baz') => {
 *   switch (type) {
 *     case 'foo':
 *       // ...
 *       return  //...
 *     case 'bar':
 *       // ...
 *       return  //...
 *     case 'baz':
 *       // ...
 *       return  //...
 *     default:
 *       return assertUnreachable(type);
 *   }
 * };
 * ```
 *
 * @param _
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function assertUnreachable(_) {
  throw new Error("Didn't expect to get here");
}

var ensureError = function ensureError(value) {
  if (value instanceof Error) return value;
  var message = "Unexpected `throw` of non-`Error` value";
  try {
    var stringified = JSON.stringify(value);
    return new Error("".concat(message, ", stringified below:\n").concat(stringified));
  } catch (_unused) {
    return new Error("".concat(message, ", unable to stringify thrown value"));
  }
};

/**
 * Simple `typeof` check for 'function' as a guard
 */
var isFunction = function isFunction(input) {
  return typeof input === 'function';
};

/**
 * Guard version of `Object.prototype.hasOwnProperty` that makes it
 * possible to access the property being checked (as `unknown`)
 *
 * @param object object to test
 * @param property name of property to test for
 */
var hasOwnProperty = function hasOwnProperty(object, property) {
  return Object.prototype.hasOwnProperty.call(object, property);
};

function _typeof(o) {
  "@babel/helpers - typeof";

  return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) {
    return typeof o;
  } : function (o) {
    return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o;
  }, _typeof(o);
}

/**
 * Simple `typeof` check for 'number' as a guard
 */
var isNumber = function isNumber(value) {
  return typeof value === 'number';
};

/**
 * Simple `typeof` check for 'string' as a guard
 */
var isString = function isString(value) {
  return typeof value === 'string';
};
var is = function is(types) {
  return function (value) {
    return types.some(function (type) {
      return _typeof(value) === type;
    });
  };
};

/**
 * Type guard helper for filtering lists that may have empty items
 *
 * @example myList.filter(isDefined)
 *
 * @param input potentially undefined value
 * @returns {boolean} whether input is neither undefined or null
 */
var isDefined = function isDefined(input) {
  return typeof input !== 'undefined' && input !== null;
};

/**
 * Type guard helper for filtering lists that may have falsey items
 *
 * @example myList.filter(isTruthy)
 *
 * @param input potentially falsey value
 * @returns {boolean} whether input is truthy
 */
var isTruthy = function isTruthy(input) {
  return typeof input !== 'undefined' && input !== null && input !== false;
};

/**
 * Conditionally invoke a function if provided value
 * is a function, otherwise simply return the value
 *
 * @param maybeFunction function or value
 */
var invoke = function invoke(maybeFunction) {
  for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    args[_key - 1] = arguments[_key];
  }
  return isFunction(maybeFunction) ? maybeFunction.apply(void 0, args) : maybeFunction;
};

/**
 * Promise-based function debounce
 *
 * @param callback function to be invoked
 * @param delay delay in milliseconds
 * @param leading whether to invoke the function immediately on the first call
 *
 * @returns tuple containing debounced function, and teardown function
 */
var debounce = function debounce(callback, delay) {
  var leading = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  var timer;
  var invoked = false;
  var returned;
  var debounced = function debounced() {
    for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      args[_key2] = arguments[_key2];
    }
    if (leading && !invoked && !timer) {
      invoked = true;
      returned = callback.apply(void 0, args);
    } else if (leading && invoked) {
      invoked = false;
    }
    return new Promise(function (resolve) {
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () {
        resolve(invoked ? returned : callback.apply(void 0, args));
        timer = null;
      }, delay);
    });
  };
  var teardown = function teardown() {
    if (timer) clearTimeout(timer);
    timer = null;
    invoked = false;
  };
  return [debounced, teardown];
};

/**
 * Get a random value within a range.
 *
 * @param maximum maximum value
 * @param minimum minimum value
 * @param round whether to round the random value to an integer
 *
 * @returns random number
 */
var random = function random(maximum, minimum) {
  var round = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
  var value = Math.random() * (maximum - minimum) + minimum;
  return round ? Math.floor(value) : value;
};

function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _toArray(arr) {
  return _arrayWithHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableRest();
}

//
// ---------- Internal --------------------
//

/**
 * Remove non-word characters from a string
 */
var removeNonWord = function removeNonWord(string) {
  return string.replace(/[^0-9a-zA-Z\xC0-\xFF -]/g, '');
};

/**
 * Format all but the first word of a string with `camelCase`
 *
 * @example
 * ```ts
 * toCamel('some-string');
 * // someString
 *
 * toCamel('Some-string');
 * // SomeString
 */
var toCamel = function toCamel(string) {
  return (
    // hyphens and underscores to spaces, then strip non-word characters
    removeNonWord(string.replace(/[-_]/g, ' ')).replace(/\s[a-z]/g, function (s) {
      return s.toUpperCase();
    }) // capitalize words
    .replace(/\s+/g, '')
  );
}; // remove spaces

//
// ---------- Public --------------------
//

/**
 * Capitalize the first character in a string
 *
 * @param string string to capitalize
 * @returns new string with first character capitalized
 */
var capitalize = function capitalize(_ref) {
  var _ref2 = _toArray(_ref),
    first = _ref2[0],
    rest = _ref2.slice(1);
  return first.toUpperCase() + rest.join('');
};

/**
 * Un-capitalize the first character in a string
 *
 * @param string string to capitalize
 * @returns new string with first character capitalized
 */
var uncapitalize = function uncapitalize(_ref3) {
  var _ref4 = _toArray(_ref3),
    first = _ref4[0],
    rest = _ref4.slice(1);
  return first.toLowerCase() + rest.join('');
};

/**
 * Format a string with `PascalCase`
 *
 * @param string string to format
 * @returns new string in `PascalCase`
 */
var pascalize = function pascalize(string) {
  return toCamel(string).replace(/^[a-z]/g, function (s) {
    return s.toLocaleUpperCase();
  });
};

/**
 * Format a string with `camelCase`
 *
 * @param string string to format
 * @returns new string in `camelCase`
 */
var camelize = function camelize(string) {
  return toCamel(string).replace(/^[A-Z]/g, function (s) {
    return s.toLocaleLowerCase();
  });
};
var split = function split(string, delimiter) {
  return string.split(delimiter);
};

export { assertUnreachable, camelize, capitalize, debounce, ensureError, hasOwnProperty, invoke, is, isDefined, isFunction, isNumber, isString, isTruthy, pascalize, random, split, uncapitalize };
